import React, { useEffect, useState, useRef } from "react";
import { styled, TextField } from "@mui/material";
import { Setting } from "../../utils/Setting";

// TODO: Migrate to google.maps.places.PlaceAutocompleteElement when react-google-autocomplete 
// library is updated or implement custom solution using the new API
// Current deprecation warning: "google.maps.places.Autocomplete is not available to new customers"
// Migration guide: https://developers.google.com/maps/documentation/javascript/places-migration-overview

/**
 * Component for Google Auto Complete Input
 * @param {object} props
 * @param {function} props.onChange - Handle the change event of Input
 * @component
 */

// Styled TextField with custom border, padding, and no shadow
const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    padding: ".7vh .7vw",
    border: "1px solid #D7D8D9",
    boxShadow: "none", // Remove box shadow

    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: "1px solid transparent", // No border on hover
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: "none", // Keep the same border color when focused
      boxShadow: "none", // Remove box shadow when focused
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none", // Remove the default border
  },
  "& .Mui-focused .MuiOutlinedInput-root": {
    boxShadow: "none", // Ensure no shadow on focus
  },
});

// Add CSS for Google Places Autocomplete dropdown
const addAutocompleteStyles = () => {
  if (document.getElementById('google-places-autocomplete-styles')) return;
  
  const style = document.createElement('style');
  style.id = 'google-places-autocomplete-styles';
  style.textContent = `
    .pac-container {
      z-index: 9999 !important;
      border-radius: 8px !important;
      box-shadow: 0 2px 6px rgba(0,0,0,0.3) !important;
      margin-top: 4px !important;
    }
    .pac-item {
      padding: 8px 12px !important;
      cursor: pointer !important;
    }
    .pac-item:hover {
      background-color: #f5f5f5 !important;
    }
    .pac-item-selected {
      background-color: #e3f2fd !important;
    }
  `;
  document.head.appendChild(style);
};

// Helper function to check if Google Maps is loaded
const isGoogleMapsLoaded = () => {
  return typeof window !== 'undefined' && 
         window.google && 
         window.google.maps && 
         window.google.maps.places;
};

function PlaceAutoComplete(props) {
  const {
    onChange = () => null,
    placeholder = "",
    disable = false,
    defaultValue = "",
    // Opt-in: only a place actually picked from Google counts as a value.
    // Free text typed into the box is shown but never reported upward, so a
    // string Google could not geocode can't reach the payload (the backend
    // rejects those with "Error converting data type nvarchar to numeric").
    // Off by default — screens that edit an already-saved address must keep
    // showing it, and clearing on focus there would wipe good data.
    requireSelection = false,
    ...rest
  } = props;

  const [isMapsLoaded, setIsMapsLoaded] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const isUserTypingRef = useRef(false);
  const isSelectingFromAutocompleteRef = useRef(false);
  const lastDefaultValueRef = useRef(defaultValue);
  const isProcessingPlaceChangeRef = useRef(false);
  const onChangeRef = useRef(onChange);

  // Add styles for autocomplete dropdown
  useEffect(() => {
    addAutocompleteStyles();
  }, []);

  // Check if Google Maps is loaded
  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 50;
    
    const checkMapsLoaded = () => {
      if (isGoogleMapsLoaded()) {
        setIsMapsLoaded(true);
        // Add a small delay to ensure autocomplete can initialize
        setTimeout(() => {
          setIsReady(true);
        }, 200);
      } else if (retryCount < maxRetries) {
        // Retry after a short delay
        retryCount++;
        setTimeout(checkMapsLoaded, 100);
      } else {
        // Still try to set ready after max retries
        setIsReady(true);
      }
    };

    checkMapsLoaded();
  }, []); // Only run once on mount

  function compIsType(t, s) {
    for (let z = 0; z < t.length; ++z) if (t[z] == s) return true;
    return false;
  }

  const [manualValue, setManualValue] = useState(defaultValue || "");
  // Whether what's in the box came from a Google suggestion. Seeded true for
  // an existing saved address so re-opening a form doesn't discard it.
  const [hasValidSelection, setHasValidSelection] = useState(!!defaultValue);
  // Mirrored in a ref because the blur handler checks this from inside a
  // timeout, where the state captured by the closure would be stale.
  const hasValidSelectionRef = useRef(!!defaultValue);
  const markSelection = (valid) => {
    hasValidSelectionRef.current = valid;
    setHasValidSelection(valid);
  };

  // Keep onChange ref up to date
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Initialize Google Places Autocomplete manually
  useEffect(() => {
    if (!isMapsLoaded || !isReady || !inputRef.current) {
      return;
    }
    
    // Prevent multiple initializations
    if (autocompleteRef.current) {
      return;
    }

    try {
      const input = inputRef.current.querySelector('input') || inputRef.current;
      
      if (!input) {
        return;
      }
      
      // Check if this input already has an autocomplete instance
      if (input.dataset.autocompleteInitialized === 'true') {
        return;
      }

      // Create autocomplete instance
      const autocomplete = new window.google.maps.places.Autocomplete(input, {
        types: ["geocode"],
        // componentRestrictions: { country: "ae" }, // Temporarily disabled for testing
      });

      // Mark input as initialized
      input.dataset.autocompleteInitialized = 'true';
      autocompleteRef.current = autocomplete;

      // Handle place selection
      const handlePlaceChanged = () => {
        // Prevent duplicate processing
        if (isProcessingPlaceChangeRef.current) {
          return;
        }
        
        const place = autocomplete.getPlace();
        
        if (!place.geometry) {
          return;
        }

        // Set flags immediately to prevent duplicate calls and manual onChange from firing
        isProcessingPlaceChangeRef.current = true;
        isSelectingFromAutocompleteRef.current = true;

        let city = "";
        if (place.address_components !== undefined) {
          let addrComp = place.address_components;
          for (let i = 0; i < addrComp.length; ++i) {
            let typ = addrComp[i].types;
            if (compIsType(typ, "locality")) {
              city = addrComp[i].long_name; //store the city
            }
          }
        }
        
        const locationValue = place?.formatted_address || place.name || "";
        const obj = {
          location: locationValue,
          lat: place?.geometry?.location?.lat(),
          lng: place?.geometry?.location?.lng(),
          city,
        };
        
        // Update state and input value synchronously
        setManualValue(locationValue);
        markSelection(true);
        if (input) {
          input.value = locationValue;
        }
        
        // Call onChange immediately with the selected place data
        onChangeRef.current(obj);

        // Reset flags after a delay
        setTimeout(() => {
          isSelectingFromAutocompleteRef.current = false;
          isProcessingPlaceChangeRef.current = false;
        }, 300);
      };

      // Listen for place selection - this is the main handler
      autocomplete.addListener('place_changed', handlePlaceChanged);

      // Set default value if provided
      if (defaultValue) {
        input.value = defaultValue;
        lastDefaultValueRef.current = defaultValue;
      }
    } catch (error) {
      // Error initializing Google Places Autocomplete
    }

    // Cleanup
    return () => {
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners?.(autocompleteRef.current);
        autocompleteRef.current = null;
      }
      if (inputRef.current) {
        const input = inputRef.current.querySelector('input') || inputRef.current;
        if (input) {
          input.dataset.autocompleteInitialized = 'false';
        }
      }
    };
  }, [isMapsLoaded, isReady]); // Remove onChange from dependencies to prevent re-initialization

  // Handle manual input changes
  const handleManualChange = (e) => {
    // Don't handle manual changes if user is selecting from autocomplete
    if (isSelectingFromAutocompleteRef.current) {
      return;
    }
    
    const value = e.target.value;
    isUserTypingRef.current = true;
    setManualValue(value);

    // Typing invalidates any earlier pick — the text no longer describes a
    // place Google resolved.
    markSelection(false);

    // In requireSelection mode the typed text stays on screen but is
    // reported upward as "no location", so an unresolvable string can never
    // be saved. Otherwise keep the original behaviour of passing it through.
    const obj = requireSelection
      ? { location: "", lat: null, lng: null, city: "" }
      : { location: value, lat: null, lng: null, city: "" };
    onChangeRef.current(obj);

    // Reset flag after a short delay
    setTimeout(() => {
      isUserTypingRef.current = false;
    }, 100);
  };

  // Update manual value when defaultValue changes (only if user hasn't modified it)
  useEffect(() => {
    // Only update if defaultValue actually changed and user isn't currently typing
    if (
      defaultValue !== undefined && 
      defaultValue !== lastDefaultValueRef.current &&
      !isUserTypingRef.current &&
      !isSelectingFromAutocompleteRef.current
    ) {
      setManualValue(defaultValue);
      lastDefaultValueRef.current = defaultValue;
      
      // Update input value if autocomplete is initialized
      if (inputRef.current && autocompleteRef.current) {
        const input = inputRef.current.querySelector('input') || inputRef.current;
        if (input) {
          input.value = defaultValue;
        }
      }
    }
  }, [defaultValue]);

  // Leftover text that never resolved to a place is cleared the moment the
  // field is focused again, so the box never shows an address the form is
  // not actually holding.
  const handleFocus = (e) => {
    if (requireSelection && !hasValidSelection && manualValue) {
      setManualValue("");

      const input =
        inputRef.current?.querySelector("input") || e?.target || null;
      if (input) {
        input.value = "";
        // Emptying `value` alone leaves Google's prediction list open and
        // still showing results for the text we just removed. Firing a real
        // input event makes its listener re-query with an empty string,
        // which is what actually closes the list.
        // (Deliberately not touching the `.pac-container` element directly:
        // forcing `display:none` on it risks overriding Google's own
        // show/hide and killing the dropdown permanently. Emptying the query
        // is the same thing a user deleting their text does, and Google
        // closes the list on its own.)
        input.dispatchEvent(new Event("input", { bubbles: true }));
      }

      onChangeRef.current({ location: "", lat: null, lng: null, city: "" });
    }
    rest?.onFocus?.(e);
  };

  // Same rule on the way out: leaving the field with text that never matched
  // a suggestion empties it, so the box can't sit there showing an address
  // the form is not holding.
  const handleBlur = (e) => {
    if (requireSelection) {
      // Picking a suggestion blurs the input *before* Google fires
      // place_changed, so clearing straight away would delete the pick the
      // user just made. Wait for that event to land, then re-check via the
      // ref (state here would be stale).
      setTimeout(() => {
        if (hasValidSelectionRef.current) return;

        const input = inputRef.current?.querySelector("input") || null;
        if (input && input.value) input.value = "";
        setManualValue("");
        onChangeRef.current({ location: "", lat: null, lng: null, city: "" });
      }, 350);
    }
    rest?.onBlur?.(e);
  };

  return (
    <StyledTextField
      ref={inputRef}
      value={manualValue}
      onChange={handleManualChange}
      placeholder={placeholder}
      disabled={disable || !isReady}
      fullWidth
      {...rest}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
}

export default PlaceAutoComplete;
